/**
 * Cookie-BFF session death vs downstream 401s.
 *
 * Portal session (sessionToken / clientSessionToken) is not the QTP OIDC
 * access_token. A 401 from /api/v1/ai-trading/* means "no trading identity",
 * not "portal cookie is dead".
 */

const SOFT_CODES = new Set([
  'plan_upgrade_required',
  'subscription_required',
  'feature_gated',
  'insufficient_plan',
  'mfa_required',
  'csrf_invalid',
  'csrf_required',
  'csrf_validation_failed',
  'qtp_oidc_required',
  'oidc_access_token_required',
])

const EXPLICIT_EXPIRED_CODES = new Set(['session_expired'])

export type SessionExpirySignal = {
  status: number
  code?: string | null
  message?: string | null
  endpoint?: string | null
}

function normalize(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
}

export function isAuthSurfaceEndpoint(endpoint: string | null | undefined): boolean {
  const path = normalize(endpoint)
  if (!path) return false
  return (
    path.includes('/auth/') ||
    path.includes('verify-client') ||
    path.includes('/session')
  )
}

function isSoftAuthFailure(code: string, message: string): boolean {
  if (SOFT_CODES.has(code)) return true
  return (
    message.includes('upgrade') ||
    message.includes('plan required') ||
    message.includes('subscription required') ||
    message.includes('csrf') ||
    message.includes('qtp_oidc') ||
    message.includes('oidc access token')
  )
}

/** Explicit portal/session-cookie death — not generic API unauthorized. */
function isExplicitSessionDead(code: string, message: string): boolean {
  if (EXPLICIT_EXPIRED_CODES.has(code)) return true
  if (message.includes('csrf')) return false
  return (
    message.includes('session expired') ||
    message.includes('token expired') ||
    message.includes('invalid token') ||
    message.includes('invalid session') ||
    message.includes('no session')
  )
}

/**
 * Whether a 401 should dispatch `session-expired` and force client logout.
 *
 * Auth surfaces (verify-client, /auth/, /session): 401 is session death
 * unless the body is a known soft/plan/CSRF/OIDC-desk code.
 *
 * All other endpoints: only explicit session-dead codes/messages.
 * Generic `unauthorized` / `authentication required` from QTP must not
 * kill a valid portal cookie.
 */
export function shouldDispatchSessionExpired(
  signal: SessionExpirySignal
): boolean {
  if (signal.status !== 401) return false
  const code = normalize(signal.code)
  const message = normalize(signal.message)
  if (isSoftAuthFailure(code, message)) return false
  if (isAuthSurfaceEndpoint(signal.endpoint)) return true
  return isExplicitSessionDead(code, message)
}
